import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import TopNav from '../components/TopNav';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api from '../lib/axios';
import { Globe, ArrowRight, Loader2, Search } from 'lucide-react';

const nicheData: Record<string, { title: string; description: string; sites: string; avgPrice: string; priceRange: string; topDomains: string[] }> = {
  technology: {
    title: 'Technology',
    description: 'Guest post opportunities on technology, SaaS, AI, software, and developer blogs.',
    sites: '42,300', avgPrice: '$180', priceRange: '$45 - $2,500',
    topDomains: ['techcrunch.com', 'mashable.com', 'thenextweb.com', 'sitepoint.com', 'dev.to', 'hackernoon.com', 'wired.com', 'theverge.com'],
  },
  finance: {
    title: 'Finance',
    description: 'Guest post sites in finance, crypto, investing, banking, and fintech.',
    sites: '18,900', avgPrice: '$500', priceRange: '$250 - $5,000',
    topDomains: ['investopedia.com', 'nerdwallet.com', 'bankrate.com', 'fool.com', 'coindesk.com', 'finance.yahoo.com'],
  },
  health: {
    title: 'Health',
    description: 'Guest post opportunities on health, wellness, medical, fitness, and nutrition sites.',
    sites: '31,200', avgPrice: '$300', priceRange: '$120 - $3,200',
    topDomains: ['healthline.com', 'webmd.com', 'medicalnewstoday.com', 'verywellhealth.com', 'mindbodygreen.com'],
  },
  news: {
    title: 'News & Media',
    description: 'Guest posts on news outlets, media publications, and journalism sites.',
    sites: '15,600', avgPrice: '$400', priceRange: '$200 - $8,000',
    topDomains: ['forbes.com', 'entrepreneur.com', 'inc.com', 'huffpost.com', 'usatoday.com', 'reuters.com'],
  },
  business: {
    title: 'Business',
    description: 'Guest post sites covering business, entrepreneurship, management, and startups.',
    sites: '28,400', avgPrice: '$250', priceRange: '$150 - $4,500',
    topDomains: ['entrepreneur.com', 'fastcompany.com', 'inc.com', 'businessinsider.com', 'hubspot.com'],
  },
  education: {
    title: 'Education',
    description: 'Guest post opportunities on education, e-learning, university, and academic sites.',
    sites: '22,100', avgPrice: '$150', priceRange: '$80 - $1,800',
    topDomains: ['edutopia.org', 'edsurge.com', 'chronicle.com', 'insidehighered.com', 'teachthought.com'],
  },
  travel: {
    title: 'Travel',
    description: 'Guest post sites about travel, tourism, adventure, and hospitality.',
    sites: '19,800', avgPrice: '$200', priceRange: '$100 - $2,200',
    topDomains: ['lonelyplanet.com', 'travelandleisure.com', 'cntraveler.com', 'nomadicmatt.com', 'theplanetd.com'],
  },
  sports: {
    title: 'Sports',
    description: 'Guest post opportunities on sports, fitness, athletics, and recreation sites.',
    sites: '14,300', avgPrice: '$180', priceRange: '$90 - $1,500',
    topDomains: ['espn.com', 'bleacherreport.com', 'sbnation.com', 'sportingnews.com', 'theringer.com'],
  },
  entertainment: {
    title: 'Entertainment',
    description: 'Guest posts on entertainment, movies, music, gaming, and pop culture sites.',
    sites: '16,700', avgPrice: '$160', priceRange: '$80 - $2,000',
    topDomains: ['variety.com', 'hollywoodreporter.com', 'ign.com', 'polygon.com', 'screenrant.com'],
  },
  'real-estate': {
    title: 'Real Estate',
    description: 'Guest post sites about real estate, property, housing market, and construction.',
    sites: '8,900', avgPrice: '$350', priceRange: '$220 - $3,800',
    topDomains: ['realtor.com', 'zillow.com', 'redfin.com', 'biggerpockets.com', 'inman.com'],
  },
  legal: {
    title: 'Legal',
    description: 'Guest post opportunities on law, legal tech, and attorney sites.',
    sites: '6,200', avgPrice: '$600', priceRange: '$300 - $5,500',
    topDomains: ['law.com', 'abajournal.com', 'lawfare.com', 'jdsupra.com', 'findlaw.com'],
  },
  fashion: {
    title: 'Fashion',
    description: 'Guest post sites covering fashion, beauty, lifestyle, and luxury.',
    sites: '12,400', avgPrice: '$120', priceRange: '$70 - $1,200',
    topDomains: ['vogue.com', 'elle.com', 'harpersbazaar.com', 'refinery29.com', 'whowhatwear.com'],
  },
};

export default function NicheCategory() {
  const { niche } = useParams<{ niche: string }>();
  const navigate = useNavigate();
  const data = nicheData[niche || ''];

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Guest Post Sites by Niche" description="Browse guest post opportunities by niche category." canonical="/niche" />
        <TopNav />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Guest Post Sites by Category</h1>
          <p className="text-slate-500 mb-8">Pick your industry. See every site that sells guest posts and what they charge.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(nicheData).map(([key, val]) => (
              <Link key={key} to={`/niche/${key}`} className="p-5 border border-slate-200 rounded-2xl hover:border-brand-400 hover:shadow-sm transition-all text-left">
                <h3 className="font-semibold text-slate-900 mb-1">{val.title}</h3>
                <p className="text-xs text-slate-500">{val.sites} sites</p>
                <p className="text-xs text-brand-600 font-medium mt-1">{val.priceRange}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${data.title} Guest Post Sites - Prices, DR & Best Deals [2026]`}
        description={`Find ${data.title.toLowerCase()} guest posting sites with real guest post prices. ${data.sites} sites available, prices from ${data.priceRange}. Compare prices from 50+ guest post marketplaces.`}
        canonical={`/niche/${niche}`}
      />
      <TopNav />

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/browse" className="hover:text-brand-600">Browse</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">{data.title}</span>
        </div>

        <h1 className="text-3xl font-extrabold text-slate-900 mb-3">{data.title} Guest Post Sites</h1>
        <p className="text-lg text-slate-500 mb-8">{data.description}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="bg-slate-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-brand-600">{data.sites}</p>
            <p className="text-xs text-slate-500 mt-1">Sites Available</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-brand-600">{data.avgPrice}</p>
            <p className="text-xs text-slate-500 mt-1">Average Price</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-brand-600">{data.priceRange.split(' - ')[0]}</p>
            <p className="text-xs text-slate-500 mt-1">Starting From</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-brand-600">50+</p>
            <p className="text-xs text-slate-500 mt-1">Sellers Compared</p>
          </div>
        </div>

        <h2 className="text-xl font-bold text-slate-900 mb-4">Popular {data.title} Domains</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
          {data.topDomains.map(domain => (
            <Link key={domain} to={`/domain/${domain}`} className="flex items-center gap-3 p-4 border border-slate-200 rounded-xl hover:border-brand-400 hover:shadow-sm transition-all">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500 uppercase">
                {domain.charAt(0)}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 text-sm">{domain}</p>
                <p className="text-xs text-slate-400">{data.title}</p>
              </div>
              <span className="text-brand-600 text-xs font-medium">View prices →</span>
            </Link>
          ))}
        </div>

        <div className="bg-brand-50 rounded-2xl p-8 text-center mb-10">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Search All {data.title} Sites</h2>
          <p className="text-slate-600 mb-4">Enter any domain to compare guest post prices from 50+ marketplaces. Our free backlink price checker shows every seller and their current rates.</p>
          <Link to="/" className="inline-flex items-center px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-colors gap-2">
            <Search className="w-4 h-4" /> Compare Prices Free
          </Link>
        </div>

        <h2 className="text-xl font-bold text-slate-900 mb-4">Other Niches</h2>
        <div className="flex flex-wrap gap-2">
          {Object.entries(nicheData).filter(([key]) => key !== niche).map(([key, val]) => (
            <Link key={key} to={`/niche/${key}`} className="px-4 py-2 border border-slate-200 rounded-full text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">
              {val.title}
            </Link>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
